import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Grid,
  useTheme,
  Typography
} from '@mui/material';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import { authenticationService } from "src/services";
import { post } from 'src/services/httpClient.service';
import { getResponsiveStyles, isMobileScreen } from 'src/common/utils/responsive';
import { useSnackbar } from 'src/components/snackbar/useSnackbar';

interface PaymentData {
  amount: string;
  currency: string;
  paymentDate: string;
  paymentGateway: string;
  paymentMethod: string;
  paymentMode: string;
  paymentStatus: string;
  transactionId: string;
  transactionType: string;
  chequeNumber: string;
  ddNumber: string;
  userId: number;
  refundAmount: string;
  refundId: string;
}

interface Errors {
  amount: boolean;
  currency: boolean;
  paymentDate: boolean;
  paymentGateway: boolean;
  paymentMethod: boolean;
  paymentMode: boolean;
  paymentStatus: boolean;
  transactionId: boolean;
  transactionType: boolean;
  chequeNumber: boolean,
  ddNumber: boolean,
  userId: boolean;
}

interface Props {
  userId: number;
  customerId: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  fetchUserDetails: () => void;
  handleClose: () => void;
}

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const UserWalletPayment: React.FC<Props> = ({
  userId,
  customerId,
  userName,
  userEmail,
  userPhone,
  fetchUserDetails,
  handleClose
}) => {
  const { vendorId, franchiseId } =
    authenticationService.getVendorFranchiseIds();

  const API_URL = process.env.REACT_APP_BASE_API_URL;
  const theme = useTheme();
  const [isMobile, setIsMobile] = useState(isMobileScreen(theme));

  const { showSnackbar } = useSnackbar();

  const [errors, setErrors] = useState<Errors>({
      amount: false, 
      currency: false, 
      paymentDate: false,
      paymentGateway: false, 
      paymentMethod: false, 
      paymentMode: false,
      paymentStatus: false, 
      transactionId: false,
      transactionType: false,
      chequeNumber: false,
      ddNumber: false,
      userId: false,
    });

  const [paymentData, setPaymentData] = useState<PaymentData>({
    amount: '',
    currency: 'INR',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentGateway: '',
    paymentMethod: '',
    paymentMode: 'ONLINE',
    paymentStatus: 'SUCCESS',
    transactionId: '',
    transactionType: 'CREDIT',
    chequeNumber: '',
    ddNumber: '',
    userId: userId,
    refundAmount: '',
    refundId: '',
  });

  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<'success' | 'error'>('success');

  useEffect(() => {
    setPaymentData(prev => ({ ...prev, userId: userId }));
  }, [userId]);

  useEffect(() => {
      const handleResize = () => {
        setIsMobile(isMobileScreen(theme));
      };
  
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [theme]);

  // const handleChange = (e: any) => {
  //   const { name, value } = e.target;
  //   setPaymentData(prev => ({ ...prev, [name]: value }));
  // };

    const handleChange = (e: any) => {
      const { name, value } = e.target;

      setPaymentData(prev => ({
        ...prev,
        [name as string]: value as string,

        ...(name === 'paymentMode'
          ? {
              paymentMethod: '',
              paymentGateway: '',
              transactionId: '',
              chequeNumber: '',
              ddNumber: ''
            }
          : {}),

        ...(name === 'paymentMethod'
          ? {
              transactionId: '',
              chequeNumber: '',
              ddNumber: ''
            }
          : {})
      }));

      if (errors[name as keyof Errors]) {
        setErrors(prev => ({
          ...prev,
          [name]: false
        }));
      }

      if (name === 'paymentMode') {
        setErrors(prev => ({
          ...prev,
          paymentGateway: false,
          paymentMethod: false,
          transactionId: false
        }));
      }

      if (name === 'paymentMethod') {
        setErrors(prev => ({
          ...prev,
          transactionId: false
        }));
      }
    };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showSnackbar('Please fill all fields correctly.', 'error');
      return;
    }

    // if (
    //   !paymentData.amount ||
    //   !paymentData.paymentGateway ||
    //   !paymentData.paymentMethod ||
    //   !paymentData.paymentStatus ||
    //   !paymentData.transactionId
    // ) {
    //   setSnackbarSeverity('error');
    //   setSnackbarMessage('Please fill all required fields');
    //   setSnackbarOpen(true);
    //   return;
    // }

    setLoading(true);

    try {
      const payload = {
        ...paymentData,
        vendorId,
        franchiseId,
        amount: Number(paymentData.amount),
        paymentDate: new Date(paymentData.paymentDate).toISOString(),
      };

      await post(`${API_URL}/api/payments`, payload);

      setSnackbarSeverity('success');
      setSnackbarMessage('Payment added successfully');
      fetchUserDetails()
      setSnackbarOpen(true);

      
      setTimeout(handleClose, 1200);
    } catch (err) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to add payment');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    let formIsValid = true;
    const newErrors: Errors = {
      amount: false, 
      currency: false, 
      paymentDate: false,
      paymentGateway: false, 
      paymentMethod: false, 
      paymentMode: false,
      paymentStatus: false, 
      transactionId: false,
      transactionType: false,
      chequeNumber: false,
      ddNumber: false,
      userId: false,
    };

    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      newErrors.amount = true;
      formIsValid = false;
    }

    if (!paymentData.currency) {
      newErrors.currency = true;
      formIsValid = false;
    }

    if (!paymentData.paymentDate) {
      newErrors.paymentDate = true;
      formIsValid = false;
    }

    // if (!paymentData.paymentGateway) {
    //   newErrors.paymentGateway = true;
    //   formIsValid = false;
    // }

    // Payment Gateway is required only for ONLINE payments
    if (
      paymentData.paymentMode === 'ONLINE' &&
      !paymentData.paymentGateway
    ) {
      newErrors.paymentGateway = true;
      formIsValid = false;
    }

    if (!paymentData.paymentMethod) {
      newErrors.paymentMethod = true;
      formIsValid = false;
    }

    if (!paymentData.paymentMode) {
      newErrors.paymentMode = true;
      formIsValid = false;
    }

    if (!paymentData.paymentStatus) {
      newErrors.paymentStatus = true;
      formIsValid = false;
    }
    
    // if (!paymentData.transactionId) {
    //   newErrors.transactionId = true;
    //   formIsValid = false;
    // }

    // ONLINE payments -> Transaction ID required
    if (
      paymentData.paymentMode === 'ONLINE' &&
      !paymentData.transactionId.trim()
    ) {
      newErrors.transactionId = true;
      formIsValid = false;
    }

    // OFFLINE BANK TRANSFER -> Transaction ID optional
    // OFFLINE CASH -> Transaction ID not required

    // OFFLINE CHEQUE -> Cheque Number required
    if (
      paymentData.paymentMode === 'OFFLINE' &&
      paymentData.paymentMethod === 'Cheque' &&
      !paymentData.chequeNumber.trim()
    ) {
      newErrors.chequeNumber = true;
      formIsValid = false;
    }

    // OFFLINE DD -> DD Number required
    if (
      paymentData.paymentMode === 'OFFLINE' &&
      paymentData.paymentMethod === 'DD' &&
      !paymentData.ddNumber.trim()
    ) {
      newErrors.ddNumber = true;
      formIsValid = false;
    }

    if (!paymentData.transactionType) {
      newErrors.transactionType = true;
      formIsValid = false;
    }

    // if (!paymentData.userId || parseInt(paymentData.userId) === 0) {
    //   newErrors.userId = true;
    //   formIsValid = false;
    // }
    console.log(newErrors)
    setErrors(newErrors);
    return formIsValid;
  };

  const transactionTypes = ['CREDIT', 'DEBIT'];
  const paymentModes = ['ONLINE', 'OFFLINE'];
  const paymentGateways = ['RazorPay', 'PhonePe', 'GPay', 'CashFree', 'PayTm', 'PayU', 'PayPal', 'CCAvenue', 'Stripe'];
  const paymentStatuses = ['SUCCESS']; //, 'PENDING', 'CREATED', 'FAILED', 'REFUNDED'
  const onlinePaymentMethods = [
    'UPI',
    'Credit Card',
    'Debit Card',
    'Net Banking',
    'Wallet'
  ];

  const offlinePaymentMethods = [
    'Cash',
    'Bank Transfer',
    'Cheque',
    'DD'
  ];

  const paymentMethods =
    paymentData.paymentMode === 'ONLINE'
      ? onlinePaymentMethods
      : offlinePaymentMethods;

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} sx={{ p: 3 }}>

        <Grid container spacing={isMobile ? 2 : 3}>
          {/* User Details */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Customer ID"
              value={customerId}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Subscriber Name"
              value={userName}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          </Grid>
          
          {/* Amount Field */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Amount *"
                name="amount"
                type="number"
                fullWidth
                value={paymentData.amount}
                onChange={handleChange}
                error={errors.amount}
                // helperText={errors.amount ? 'Amount is required and must be greater than 0.' : ''}
                disabled={loading}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 0.5 }}>₹</Typography>,
                }}
              />
            </Grid>

            {/* Currency */}
            <Grid item xs={12} sm={4}>
              <TextField
                label="Currency *"
                name="currency"
                fullWidth
                value={paymentData.currency}
                onChange={handleChange}
                error={errors.currency}
                helperText={errors.currency ? 'Currency is required.' : ''}
                disabled={loading}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>

            {/* Transaction Type */}
            <Grid item xs={12} sm={4}>

              <FormControl fullWidth error={errors.transactionType}>
                <InputLabel>Transaction Type *</InputLabel>
                <Select
                  name="transactionType"
                  value={paymentData.transactionType}
                  onChange={handleChange}
                  label="Payment Mode *"
                  disabled={loading}
                >
                  {transactionTypes.map((mode) => (
                    <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                  ))}
                </Select>
                {errors.transactionType && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    Transaction Type is required.
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Payment Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Payment Date *"
                name="paymentDate"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={paymentData.paymentDate}
                onChange={handleChange}
                error={errors.paymentDate}
                helperText={errors.paymentDate ? 'Payment date is required.' : ''}
                disabled={loading}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            
            {/* Payment Mode */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={errors.paymentMode}>
                <InputLabel>Payment Mode *</InputLabel>
                <Select
                  name="paymentMode"
                  value={paymentData.paymentMode}
                  onChange={handleChange}
                  label="Payment Mode *"
                  disabled={loading}
                >
                  {paymentModes.map((mode) => (
                    <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                  ))}
                </Select>
                {errors.paymentMode && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    Payment mode is required.
                  </Typography>
                )}
              </FormControl>
            </Grid>

            
            {/* Payment Method */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={errors.paymentMethod}>
                <InputLabel>Payment Method *</InputLabel>

                <Select
                  name="paymentMethod"
                  value={paymentData.paymentMethod}
                  onChange={handleChange}
                  label="Payment Method *"
                  disabled={loading}
                >
                  {paymentMethods.map((method) => (
                    <MenuItem key={method} value={method}>
                      {method}
                    </MenuItem>
                  ))}
                </Select>

                {errors.paymentMethod && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    Payment method is required.
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Payment Gateway */}
            { paymentData.paymentMode === 'ONLINE' && (
              <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={errors.paymentGateway}>
                <InputLabel>
                  Payment Gateway{paymentData.paymentMode === 'ONLINE' ? ' *' : ''}
                </InputLabel>

                <Select
                  name="paymentGateway"
                  value={paymentData.paymentGateway}
                  onChange={handleChange}
                  label={`Payment Gateway${paymentData.paymentMode === 'ONLINE' ? ' *' : ''}`}
                  // disabled={loading || paymentData.paymentMode === 'OFFLINE'}
                >
                  {paymentGateways.map((gateway) => (
                    <MenuItem key={gateway} value={gateway}>
                      {gateway}
                    </MenuItem>
                  ))}
                </Select>

                {errors.paymentGateway && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    Payment gateway is required for ONLINE payments.
                  </Typography>
                )}
              </FormControl>
            </Grid>
            )}

            {/* Transaction ID */}
            { (paymentData.paymentMode === 'ONLINE' || (paymentData.paymentMode === 'OFFLINE' &&
                      paymentData.paymentMethod === 'Bank Transfer')) && (<Grid item xs={12} sm={6}>
              <TextField
                label={`Transaction ID${
                  paymentData.paymentMode === 'ONLINE' ? ' *' : ''
                }`}
                name="transactionId"
                fullWidth
                value={paymentData.transactionId}
                onChange={handleChange}
                error={errors.transactionId}
                helperText={
                  errors.transactionId
                    ? 'Transaction ID is required'
                    : paymentData.paymentMode === 'OFFLINE' &&
                      paymentData.paymentMethod === 'Bank Transfer'
                    ? 'Transaction ID is optional.'
                    : ''
                }
                disabled={
                  loading ||
                  (paymentData.paymentMode === 'OFFLINE' &&
                    paymentData.paymentMethod !== 'Bank Transfer')
                }
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
          )}
            
            {paymentData.paymentMode === 'OFFLINE' &&
              paymentData.paymentMethod === 'Cheque' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Cheque Number *"
                    name="chequeNumber"
                    fullWidth
                    value={paymentData.chequeNumber}
                    onChange={handleChange}
                    error={errors.chequeNumber}
                    helperText={
                      errors.chequeNumber
                        ? 'Cheque Number is required.'
                        : ''
                    }
                    disabled={loading}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
              )}

            {paymentData.paymentMode === 'OFFLINE' &&
              paymentData.paymentMethod === 'DD' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="DD Number *"
                    name="ddNumber"
                    fullWidth
                    value={paymentData.ddNumber}
                    onChange={handleChange}
                    error={errors.ddNumber}
                    helperText={
                      errors.ddNumber
                        ? 'DD Number is required.'
                        : ''
                    }
                    disabled={loading}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
              )}

            {/* Payment Status */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={errors.paymentStatus}>
                <InputLabel>Payment Status *</InputLabel>
                <Select
                  name="paymentStatus"
                  value={paymentData.paymentStatus}
                  onChange={handleChange}
                  label="Payment Status *"
                  disabled={loading}
                >
                  {paymentStatuses.map((status) => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
                {errors.paymentStatus && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    Payment status is required.
                  </Typography>
                )}
              </FormControl>
            </Grid>
        </Grid>


          <Box 
            sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: 2, 
              mt: 4,
              flexDirection: isMobile ? 'column-reverse' : 'row'
            }}
          >
        {/* Submit */}

          <Button
            type="submit"
            fullWidth={isMobile}
            sx={{ py: isMobile ? 1 : 1.5 }}
            variant="contained"
            // sx={{ mt: 3 }}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Submit Payment'}
          </Button>

          {/* Cancel */}
          <Button
            // fullWidth
            variant="outlined"
            // sx={{ mt: 2 }}
            onClick={handleClose}
            disabled={loading}
            fullWidth={isMobile}
            sx={{ py: isMobile ? 1 : 1.5 }}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserWalletPayment;
